import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { artifactContent, listAppArtifacts } from '@/features/apps/artifacts-client';
import type { CachedConversation } from '@/lib/conversations/conversation-cache';
import { transcriptEntry } from '@/lib/conversations/conversation-cache';
import type { useSidebarModel } from '@/lib/desktop-shell/use-sidebar-model';
import { getLocalConversation } from '@/lib/local-conversations';
import { mentionArtifact, offeredApp, offeredArtifact } from '@/lib/side-panel/mention-targets';

type SidebarThread = ReturnType<typeof useSidebarModel>['threads'][number];

interface ConversationReferenceResolverOptions {
  resolverRef: React.MutableRefObject<
    (kind: MentionTarget['kind'], id: string) => Promise<unknown>
  >;
  chatId: string;
  conversationTitle: string;
  messages: readonly CrowdexUIMessage[];
  conversationCache: Record<string, CachedConversation>;
  sidebarThreads: readonly SidebarThread[];
  visibleAgentTerminals: readonly AgentTerminalDisplay[];
  panelMentionTargets: readonly MentionTarget[];
}

/** Keeps ACP reference resolution pointed at the latest conversation and panel state. */
export function useConversationReferenceResolver({
  resolverRef,
  chatId,
  conversationTitle,
  messages,
  conversationCache,
  sidebarThreads,
  visibleAgentTerminals,
  panelMentionTargets,
}: ConversationReferenceResolverOptions): void {
  resolverRef.current = async (kind, id) => {
    if (kind === 'chat') {
      const thread = sidebarThreads.find((candidate) => candidate.id === id);
      if (id === chatId) {
        return { id, title: conversationTitle, messages: messages.map(transcriptEntry) };
      }
      const cached = conversationCache[id];
      if (cached !== undefined) {
        return { id, title: cached.title, messages: cached.messages.map(transcriptEntry) };
      }
      const data =
        thread?.runtime === 'cloud'
          ? await apiFetch<{ messages: readonly { role: string; parts: unknown }[] }>(
              `/api/sync/messages?threadId=${encodeURIComponent(id)}`,
              { credentials: 'include' },
            )
          : await getLocalConversation(id);
      if (data === null)
        throw new Error('This local conversation is not available on this device.');
      return {
        id,
        title: thread?.title ?? 'Conversation',
        messages: data.messages.map(transcriptEntry),
      };
    }
    if (kind === 'app') {
      // An installed app: its title and the skills that say how to use it.
      const app = offeredApp(id);
      return app === undefined ? { kind: 'app', plugin: id } : { kind: 'app', ...app };
    }
    if (kind === 'terminal') {
      const terminal = visibleAgentTerminals.find((candidate) => candidate.id === id);
      if (terminal !== undefined) return terminal;
    }
    const target = panelMentionTargets.find(
      (candidate) => candidate.kind === kind && candidate.id === id,
    );
    if (kind === 'artifact') {
      const artifact = target === undefined ? undefined : mentionArtifact(target);
      return await describeArtifact(artifact ?? undefined, id);
    }
    if (target !== undefined) return target.value ?? target;
    throw new Error(`Unknown Crowdex ${kind} reference: ${id}`);
  };
}

/** At most this much of a `drive:` or `plugin:` artifact's entry goes to the agent with it. */
const CONTENT_CHARS = 64 * 1024;

/**
 * An artifact reference for the agent. A `workspace:` one it reads with its own tools; a `drive:`
 * or `plugin:` one (a template, a design system) comes with its entry file's text. A template says
 * what it is for: the user starts something new from it.
 */
export async function describeArtifact(
  listed: ArtifactListingEntry | undefined,
  reference: string,
): Promise<unknown> {
  let id = reference;
  try {
    id = decodeURIComponent(reference);
  } catch {
    // Kept as is.
  }
  const artifact =
    listed ??
    offeredArtifact(id) ??
    (await listAppArtifacts().catch(() => [])).find((entry) => entry.id === id);
  if (artifact === undefined) return { kind: 'artifact', id };
  // What a plugin ships (a template, a design system) is read-only: a copy is what changes.
  const purpose =
    artifact.space === 'plugin'
      ? {
          instructions: `Read-only: shipped by ${artifact.pluginTitle}. To change it or make something from it, duplicate it first with ${artifact.pluginTitle}'s tools (its space and path) and change the copy.`,
        }
      : {};
  if (artifact.space === 'workspace') return { ...artifact, ...purpose };
  const read = await artifactContent({
    space: artifact.space,
    path: artifact.entry,
    plugin: artifact.plugin,
  }).catch(() => null);
  return {
    ...artifact,
    ...(read?.encoding === 'utf8' ? { content: read.content.slice(0, CONTENT_CHARS) } : {}),
    ...purpose,
  };
}
